import axios from "axios";

// Clinical registries API (notes = type 1, summaries = type 2).
//
// Reads are done on the patient (a clinical file lists every registry made about
// that patient); writes are done on the professional (the author owns create /
// update / delete). See openapi: /patients/{nhsId}/clinical-registries and
// /professionals/clinical-registries.

// Privacy is stored as an integer on the backend:
//   ONLY_ME = 1, CLASSIFIED = 2, ALL = 99
export enum RegistryPrivacy {
  ONLY_ME = 1,
  CLASSIFIED = 2,
  ALL = 99
}

// type discriminator on a ClinicalRegistry
export enum RegistryType {
  NOTE = 1,
  SUMMARY = 2
}

export interface ClinicalRegistry {
  id: string;
  clinicalFileId?: string;
  type: RegistryType;
  privacy: number;
  viewableByPatient: boolean;
  shortText?: string;
  formattedText?: string;
  createdBy: string;
  updatedBy?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface CreateClinicalRegistryPayload {
  id: string;
  clinicalFileId: string;
  type: RegistryType;
  privacy: number;
  viewableByPatient: boolean;
  createdAt: string;
  shortText: string;
  formattedText: string;
}

export interface UpdateClinicalRegistryPayload {
  privacy?: number;
  viewableByPatient?: boolean;
  shortText?: string;
  formattedText?: string;
}

// Strips the rich-text HTML down to a plain-text `shortText`, mirroring the
// React Native app so both clients produce the same value: closing block tags
// and <br> become newlines, the remaining inline tags are dropped, and &nbsp;
// becomes a plain space.
const SHORT_TEXT_TRANSFORMATIONS: ReadonlyArray<[
  RegExp,
  string
]> = Object.freeze([
  [/<\/(span|div|li)>|<br>/g, "\n"],
  [/<\/?(span|div|ol|i|b|li)[^>]*>|<\/?sup>|<\/?sub>/g, ""],
  [/&nbsp;/g, " "]
]);

export function extractShortText(html: string): string {
  let cleanText = html;
  SHORT_TEXT_TRANSFORMATIONS.forEach(([search, replace]) => {
    cleanText = cleanText.replace(search, replace);
  });
  return cleanText;
}

const authHeaders = (token: string) => ({
  headers: { Authorization: `Bearer ${token}` }
});

const API = process.env.REACT_APP_API_URL;

// ── Reads (on the patient) ────────────────────────────────────────────────────

export const listPatientClinicalRegistries = async (
  token: string,
  nhsId: string | number
): Promise<ClinicalRegistry[]> => {
  const { data } = await axios.get(
    `${API}/api/patients/${nhsId}/clinical-registries`,
    authHeaders(token)
  );
  return data.clinicalRegistries ?? [];
};

export const getPatientClinicalRegistry = async (
  token: string,
  nhsId: string | number,
  id: string
): Promise<ClinicalRegistry> => {
  const { data } = await axios.get(
    `${API}/api/patients/${nhsId}/clinical-registries/${id}`,
    authHeaders(token)
  );
  return data.clinicalRegistry;
};

// ── Writes (on the professional) ──────────────────────────────────────────────

export const createClinicalRegistry = async (
  token: string,
  payload: CreateClinicalRegistryPayload
): Promise<ClinicalRegistry> => {
  const { data } = await axios.post(
    `${API}/api/professionals/clinical-registries`,
    payload,
    authHeaders(token)
  );
  return data.clinicalRegistry;
};

export const updateClinicalRegistry = async (
  token: string,
  id: string,
  payload: UpdateClinicalRegistryPayload
): Promise<ClinicalRegistry> => {
  const { data } = await axios.patch(
    `${API}/api/professionals/clinical-registries/${id}`,
    payload,
    authHeaders(token)
  );
  return data.clinicalRegistry;
};

export const deleteClinicalRegistry = async (
  token: string,
  id: string
): Promise<void> => {
  await axios.delete(
    `${API}/api/professionals/clinical-registries/${id}`,
    authHeaders(token)
  );
};
